import { useCallback, useEffect, useState } from 'react'
import { Search } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Input } from '@/components/ui/Input'
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable'
import { Pagination } from '@/components/ui/Pagination'
import { type PageSize } from '@/hooks/useCrudList'
import type { PaginationMeta } from '@/types/api'
import { toast } from '@/components/ui/Toast'
import { brl } from '@/lib/format'
import { cn } from '@/lib/cn'
import { exportToXlsx, type ExportColumn } from '@/lib/exportXlsx'
import {
  RevenuesService,
  type CommissionsReport, type CommissionTransaction, type CommissionDetail,
} from '@/services/modules/RevenuesService'
import { currentMonthRange, ReportFilters } from './reportShared'
import { CommissionDetailDrawer } from '@/components/CommissionDetailDrawer'

const ROLE_LABELS: Record<string, string> = {
  DIRETOR: 'Diretores',
  SUPERVISOR: 'Supervisores',
  AFILIADO: 'Afiliados',
}

const ROLE_FILTERS: { value: string; label: string }[] = [
  { value: '', label: 'Todos' },
  { value: 'DIRETOR', label: 'Diretores' },
  { value: 'SUPERVISOR', label: 'Supervisores' },
  { value: 'AFILIADO', label: 'Afiliados' },
]

/** Colunas da exportação Excel (valores numéricos, como no extrato da fatura). */
const EXPORT_COLUMNS: ExportColumn<CommissionTransaction>[] = [
  { header: 'Comissionado',      accessor: r => r.userName },
  { header: 'Papel',             accessor: r => ROLE_LABELS[r.type] ?? r.type },
  { header: 'Incidências',       accessor: r => r.count },
  { header: 'Pendente (R$)',     accessor: r => r.pendente },
  { header: 'Consolidado (R$)',  accessor: r => r.consolidado },
  { header: 'Total (R$)',        accessor: r => r.total },
]

/** Tamanho da página usada para buscar "todos os registros" na exportação. */
const EXPORT_PAGE_SIZE = 500

export function RevenueReportCommissionsPage() {
  const init = currentMonthRange()
  const [from, setFrom] = useState(init.from)
  const [to, setTo] = useState(init.to)
  // Período "aplicado" (dispara reloads); separado dos inputs.
  const [applied, setApplied] = useState({ from: init.from, to: init.to })

  const [summary, setSummary] = useState<CommissionsReport | null>(null)
  const [loadingSummary, setLoadingSummary] = useState(false)

  // Transações
  const [type, setType] = useState('')
  const [search, setSearch] = useState('')        // input (digitação)
  const [searchQuery, setSearchQuery] = useState('') // aplicado (debounced)
  const [page, setPage] = useState(1)
  const [size, setSize] = useState<PageSize>(25)
  const [rows, setRows] = useState<CommissionTransaction[]>([])
  const [meta, setMeta] = useState<PaginationMeta | null>(null)
  const [loadingTx, setLoadingTx] = useState(false)

  // Debounce do filtro por comissionado.
  useEffect(() => {
    const t = setTimeout(() => { setSearchQuery(search); setPage(1) }, 350)
    return () => clearTimeout(t)
  }, [search])

  const loadSummary = useCallback(async (f: string, t: string) => {
    setLoadingSummary(true)
    try { setSummary(await RevenuesService.reportsCommissions(f, t)) }
    catch { toast.error('Erro ao carregar o resumo de comissionamento.') }
    finally { setLoadingSummary(false) }
  }, [])

  const loadTx = useCallback(async (f: string, t: string, ty: string, q: string, p: number, s: number) => {
    setLoadingTx(true)
    try {
      const res = await RevenuesService.reportsCommissionTransactions({ from: f, to: t, type: ty || undefined, q: q || undefined, page: p, size: s })
      setRows(res.data)
      setMeta(res.meta)
    } catch { toast.error('Erro ao carregar as transações.') }
    finally { setLoadingTx(false) }
  }, [])

  // Carrega ao aplicar período
  useEffect(() => { loadSummary(applied.from, applied.to) }, [loadSummary, applied])
  // Carrega transações ao mudar período/tipo/busca/página/tamanho
  useEffect(() => { loadTx(applied.from, applied.to, type, searchQuery, page, size) }, [loadTx, applied, type, searchQuery, page, size])

  function apply() {
    setApplied({ from, to })
    setPage(1)
  }

  // Exportação Excel — "página atual" usa as linhas da tela; "todos" percorre as
  // páginas do endpoint com os filtros aplicados (período, papel e busca).
  const [exporting, setExporting] = useState(false)
  const exportFilename = 'comissionamento'

  function exportPage() {
    if (!rows.length) { toast.error('Nenhum registro para exportar.'); return }
    exportToXlsx(exportFilename, rows, EXPORT_COLUMNS)
  }

  async function exportAll() {
    if (exporting) return
    setExporting(true)
    const loadingId = toast.loading({ title: 'Exportando', description: 'Buscando todos os comissionados do período.' })
    try {
      const all: CommissionTransaction[] = []
      let p = 1
      let lastPage = 1
      do {
        const res = await RevenuesService.reportsCommissionTransactions({
          from: applied.from, to: applied.to, type: type || undefined, q: searchQuery || undefined, page: p, size: EXPORT_PAGE_SIZE,
        })
        all.push(...res.data)
        lastPage = res.meta?.last_page ?? 1
        if (!res.data.length) break
        p++
      } while (p <= lastPage)
      if (!all.length) { toast.error('Nenhum registro para exportar.'); return }
      exportToXlsx(exportFilename, all, EXPORT_COLUMNS)
    } catch {
      toast.error('Erro ao exportar o comissionamento.')
    } finally {
      toast.dismiss(loadingId)
      setExporting(false)
    }
  }

  // Drawer de detalhamento (clique em Pendente/Consolidado).
  const [drawer, setDrawer] = useState<{ row: CommissionTransaction; status: 'consolidado' | 'pendente' } | null>(null)
  const [detail, setDetail] = useState<CommissionDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const openDetail = useCallback(async (row: CommissionTransaction, status: 'consolidado' | 'pendente') => {
    setDrawer({ row, status })
    setDetail(null)
    setDetailLoading(true)
    try { setDetail(await RevenuesService.reportsCommissionDetail({ from: applied.from, to: applied.to, userId: row.userId, type: row.type, status })) }
    catch { toast.error('Erro ao carregar o detalhamento.') }
    finally { setDetailLoading(false) }
  }, [applied])

  const valueBtn = (active: boolean, cls: string) => cn('cursor-pointer underline-offset-2 hover:underline disabled:cursor-default disabled:no-underline', active ? cls : 'text-gray-300')

  const columns: DataTableColumn<CommissionTransaction>[] = [
    { key: 'user', header: 'Comissionado', alwaysVisible: true, render: r => <span className="font-medium text-gray-800">{r.userName}</span> },
    { key: 'type', header: 'Papel', align: 'center', className: 'w-32', render: r => <span className="text-gray-600">{ROLE_LABELS[r.type] ?? r.type}</span> },
    { key: 'count', header: 'Incidências', align: 'center', className: 'w-28', render: r => <span className="text-gray-500">{r.count}</span> },
    { key: 'pendente', header: 'Pendente', align: 'right', className: 'w-32', render: r => (
      <button type="button" disabled={r.pendente <= 0} onClick={() => openDetail(r, 'pendente')} className={valueBtn(r.pendente > 0, 'font-medium text-amber-600')}>{brl(r.pendente)}</button>
    ) },
    { key: 'consolidado', header: 'Consolidado', align: 'right', className: 'w-32', render: r => (
      <button type="button" disabled={r.consolidado <= 0} onClick={() => openDetail(r, 'consolidado')} className={valueBtn(r.consolidado > 0, 'font-medium text-green-600')}>{brl(r.consolidado)}</button>
    ) },
    { key: 'total', header: 'Total', align: 'right', className: 'w-32', render: r => <span className="font-semibold text-gray-800">{brl(r.total)}</span> },
  ]

  return (
    <>
      <PageHeader title="Comissionamento" subtitle="Valores a pagar por papel, no período"
        crumbs={[{ label: 'Financeiro' }, { label: 'Relatórios' }, { label: 'Comissionamento' }]}
      />
      <ReportFilters from={from} to={to} onFrom={setFrom} onTo={setTo} onApply={apply} loading={loadingSummary || loadingTx} />

      {/* Resumo por papel */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 mb-6">
        {(summary?.byType ?? ROLE_FILTERS.slice(1).map(r => ({ type: r.value, total: 0, pendente: 0, consolidado: 0, count: 0 }))).map((c) => (
          <div key={c.type} className="rounded-md bg-white ring-1 ring-gray-200 p-5">
            <div className="text-[11px] uppercase tracking-wider text-gray-500">{ROLE_LABELS[c.type] ?? c.type}</div>
            <div className="text-2xl font-semibold text-gray-800 mt-1">{brl(c.total)}</div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
              <div className="rounded bg-amber-50 px-2 py-1.5">
                <div className="text-amber-700 font-semibold">{brl(c.pendente)}</div>
                <div className="text-gray-500">Pendente</div>
              </div>
              <div className="rounded bg-green-50 px-2 py-1.5">
                <div className="text-green-700 font-semibold">{brl(c.consolidado)}</div>
                <div className="text-gray-500">Consolidado</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Transações */}
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-gray-700">Por comissionado</h3>
        <div className="flex flex-wrap items-center gap-3">
          <div className="w-full sm:w-64">
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Filtrar comissionado" iconLeft={<Search size={14} />} clearable />
          </div>
          <div className="inline-flex rounded-md ring-1 ring-gray-200 bg-white overflow-hidden">
          {ROLE_FILTERS.map((opt, i) => {
            const active = type === opt.value
            return (
              <button
                key={opt.value || 'all'}
                type="button"
                onClick={() => { setType(opt.value); setPage(1) }}
                className={cn(
                  'cursor-pointer px-3 py-1.5 text-[13px] font-medium transition min-w-[7.5rem] text-center',
                  i > 0 && 'border-l border-gray-200',
                  active ? 'bg-brand-500 text-white hover:bg-brand-600' : 'text-gray-600 hover:bg-gray-50',
                )}
              >
                {opt.label}
              </button>
            )
          })}
          </div>
        </div>
      </div>

      <DataTable
        tableId="financial.report-commission-transactions"
        exportable
        onExportPage={exportPage}
        onExportAll={exportAll}
        columns={columns}
        rows={rows}
        loading={loadingTx}
        getRowKey={r => r.id}
        emptyMessage="Nenhum comissionamento no período."
      />
      <Pagination meta={meta} size={size} onChange={setPage} onSizeChange={setSize} />

      <CommissionDetailDrawer
        open={drawer !== null}
        onClose={() => setDrawer(null)}
        title={drawer ? `${drawer.row.userName} — ${drawer.status === 'pendente' ? 'Pendente' : 'Consolidado'}` : 'Detalhamento'}
        subtitle="O que a empresa paga a este comissionado, fatura a fatura"
        loading={detailLoading}
        detail={detail}
      />
    </>
  )
}

export default RevenueReportCommissionsPage
